import { X, FolderKanban, ExternalLink, Paperclip, FileText, AlertTriangle, CheckCircle2, Circle, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DOWNLOAD_BASE_URL, API_BASE_URL } from "../../lib/api";

const fileUrl = (u: string) => (/^https?:\/\//.test(u) ? u : `${DOWNLOAD_BASE_URL.replace(/\/$/, "")}/${u.replace(/^\//, "")}`);

// R2-stored files are private → link to the authenticated presigned-redirect
// endpoint. Local files link straight to the static path.
const attachmentHref = (projectId: string, a: any) =>
  a.r2Key
    ? `${API_BASE_URL}/project/${projectId}/attachments/${a._id}/download`
    : fileUrl(a.url);

const money = (n: any) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const fmtDate = (d?: string | Date) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const totalPrice = (p: any) => {
  if (p?.projectPrice && Number(p.projectPrice) > 0) return Number(p.projectPrice);
  return (Number(p?.escrowAmount) || 0) + (Number(p?.releasedPayment) || 0);
};

const msColor = (s?: string) =>
  s === "Released" ? "text-emerald-600" : s === "In Escrow" ? "text-amber-600" : "text-slate-500";

export default function ProjectDetailDrawer({
  project,
  onClose,
}: {
  project: any | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  if (!project) return null;
  const ms = project.milestones || [];
  const contactName =
    typeof project.assignedContact === "object" ? project.assignedContact?.name : undefined;
  const attachments = project.attachments || [];
  const checklist = project.requirementChecklist || [];
  const isUpwork = (project.source || "Upwork") !== "Direct";

  // Milestone governance status.
  const anyReleased = ms.some((m: any) => m.status === "Released");
  const nextPendingIdx = ms.findIndex((m: any) => m.status === "Pending");
  const firstFunded = ms.length > 0 && (ms[0].status === "In Escrow" || ms[0].status === "Released");
  const notStarted = /not started|pending/i.test(project.projectStatus || "");
  let govNote: string | null = null;
  if (isUpwork) {
    if (notStarted && ms.length > 0 && !firstFunded) {
      govNote = "Ask the client to escrow the first milestone before starting this project.";
    } else if (anyReleased && nextPendingIdx !== -1) {
      govNote = `Milestone ${nextPendingIdx + 1} ("${ms[nextPendingIdx].title}") must be funded in escrow before work continues.`;
    }
  }

  const Row = ({ label, value }: { label: string; value: any }) => (
    <div className="flex justify-between items-center py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800">{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1 mb-0.5">
              <FolderKanban size={12} /> {project.projectType || "Project"}
              {project.projectSubType ? ` · ${project.projectSubType}` : ""} · {project.source || "Upwork"}
            </p>
            <h2 className="text-lg font-bold text-slate-900 leading-tight break-words">{project.name}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 shrink-0">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {govNote && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-[12px] text-amber-800">{govNote}</p>
            </div>
          )}

          <section className="rounded-xl border border-slate-100 bg-slate-50/50 p-3">
            <Row label="Status" value={project.projectStatus || "—"} />
            <Row label="Total Price" value={money(totalPrice(project))} />
            <Row label="Payment Status" value={project.paymentStatus || "—"} />
            <Row label="Escrow" value={money(project.escrowAmount)} />
            <Row label="Released" value={money(project.releasedPayment)} />
            {contactName && <Row label="Contact" value={contactName} />}
            <Row label="ETA" value={fmtDate(project.eta)} />
            <Row label="Created" value={fmtDate(project.createdAt)} />
          </section>

          {ms.length > 0 && (
            <section>
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Milestones</h3>
              <div className="space-y-1.5">
                {ms.map((m: any, i: number) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                    <span className="font-medium text-slate-800 truncate">{m.title || `Milestone ${i + 1}`}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="font-semibold">{money(m.amount)}</span>
                      <span className={`text-[11px] font-semibold ${msColor(m.status)}`}>{m.status}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Linked records (traceability) */}
          {(project.proposal || project.sourceBid || project.upworkJob) && (
            <section>
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Linked Records</h3>
              <div className="flex flex-wrap gap-1.5">
                {project.proposal && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-slate-100 text-slate-700 px-2 py-1 rounded-full"><Link2 size={12} /> Proposal</span>
                )}
                {project.sourceBid && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full"><Link2 size={12} /> Won Bid</span>
                )}
                {project.upworkJob && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-100 text-blue-700 px-2 py-1 rounded-full"><Link2 size={12} /> Upwork Job</span>
                )}
              </div>
            </section>
          )}

          {/* Requirement checklist */}
          {checklist.length > 0 && (
            <section>
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Onboarding Requirements</h3>
              <div className="space-y-1">
                {checklist.map((c: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {c.met ? <CheckCircle2 size={15} className="text-emerald-600 shrink-0" /> : <Circle size={15} className="text-slate-300 shrink-0" />}
                    <span className={c.met ? "text-slate-800" : "text-slate-500"}>
                      {c.label}{c.required && !c.met && <span className="text-amber-600 text-[11px]"> · missing</span>}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <section>
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
                <Paperclip size={12} /> Documents
              </h3>
              <div className="space-y-1.5">
                {attachments.map((a: any, i: number) => (
                  <a
                    key={i}
                    href={attachmentHref(project._id, a)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    <span className="flex items-center gap-2 min-w-0 text-slate-800">
                      <FileText size={14} className="text-primary shrink-0" />
                      <span className="truncate">{a.name}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-semibold uppercase text-slate-400">{a.category}</span>
                      <ExternalLink size={13} className="text-slate-400" />
                    </span>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 shrink-0">
          <button
            onClick={() => { onClose(); navigate("/projects"); }}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90"
          >
            Open in Projects <ExternalLink size={15} />
          </button>
        </div>
      </aside>
    </div>
  );
}
